import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Wifi, CalendarDays } from "lucide-react";
import HankoSeal from "./HankoSeal";
import { RevealLine } from "./Reveal";
import { scrollToId } from "./Navbar";

export default function Hero() {
  const { scrollY } = useScroll();
  const yKanji = useTransform(scrollY, [0, 900], [0, 220]);
  const yPhoto = useTransform(scrollY, [0, 900], [0, 80]);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16">
      <motion.img
        aria-hidden
        src="/kanji-sora.png"
        alt=""
        style={{ y: yKanji }}
        className="pointer-events-none select-none absolute -right-6 sm:right-8 top-20 w-[15rem] sm:w-[22rem] lg:w-[28rem] opacity-25"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 w-full relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="inline-flex items-center gap-2 border border-hanko/40 bg-hanko/[0.07] rounded-full px-4 py-2 mb-8"
              data-testid="hero-school-year-badge"
            >
              <span className="w-2 h-2 rounded-full bg-hanko animate-pulse" />
              <span className="text-xs font-mono tracking-widest uppercase text-hanko">
                An școlar 2026–2027 · start 15 octombrie
              </span>
            </motion.div>

            <h1 className="font-serif text-sumi text-5xl sm:text-7xl lg:text-[5.5rem] leading-[1.02] tracking-tight">
              <RevealLine delay={0.25}>Același cer.</RevealLine>
              <RevealLine delay={0.45}>
                <span className="italic text-hanko">Două culturi.</span>
              </RevealLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-7 max-w-xl text-base sm:text-lg text-charcoal/80 leading-relaxed"
              data-testid="hero-subtext"
            >
              SORA înseamnă „cer" în japoneză — același cer deasupra Bucureștiului și a Tokyo-ului.
              Învățăm limba japoneză împreună cu cultura din spatele ei: caligrafie, ceremonia ceaiului, obiceiuri.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <button
                data-testid="hero-cta-trial-button"
                onClick={() => scrollToId("#lectie")}
                className="group bg-hanko text-washi font-semibold px-8 py-4 rounded-full text-base hover:bg-hankodeep transition-colors duration-300 flex items-center justify-center gap-2"
              >
                Programează o lecție gratuită
                <ArrowDown size={18} className="transition-transform duration-300 group-hover:translate-y-1" />
              </button>
              <button
                data-testid="hero-cta-courses-button"
                onClick={() => scrollToId("#cursuri")}
                className="border border-sumi/25 text-sumi font-semibold px-8 py-4 rounded-full text-base hover:border-hanko hover:text-hanko transition-colors duration-300"
              >
                Vezi cursurile
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.35 }}
              className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono tracking-wider uppercase text-charcoal/60"
              data-testid="hero-info-badges"
            >
              <span className="flex items-center gap-2"><MapPin size={13} className="text-hanko" /> Online sau față în față</span>
              <span className="flex items-center gap-2"><Wifi size={13} className="text-hanko" /> Grupe de 6–10 elevi</span>
              <span className="flex items-center gap-2"><CalendarDays size={13} className="text-hanko" /> An școlar din 15 oct 2026</span>
            </motion.div>

            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.1, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 relative lg:hidden aspect-[4/5] max-w-xs"
            >
              <img
                src="/hero-sora-event.webp"
                alt="Elevi SORA la un eveniment — fetiță în yukata cu pliantul Discover Japan"
                className="w-full h-full object-cover"
                data-testid="hero-image-mobile"
              />
              <div className="absolute inset-0 ring-1 ring-sumi/10" />
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative hidden lg:block">
            <motion.div
              style={{ y: yPhoto }}
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.3, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[3/4] max-w-sm ml-auto"
            >
              <img
                src="/hero-sora-event.webp"
                alt="Elevi SORA la un eveniment — fetiță în yukata cu pliantul Discover Japan"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 ring-1 ring-sumi/10" />
            </motion.div>
            <div className="absolute -bottom-8 -left-4 animate-seal-drift">
              <HankoSeal size={110} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
